import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Popover,
  Typography,
} from "@material-ui/core";
import { Add, Edit, ImportExport } from "@material-ui/icons";
import { GridCellParams, GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import { useHeader } from "components/Layout/HeaderContext";
import PageableTable from "components/PageableTable/PageableTable";
import { RootState } from "config/store";
import supportedLocales from "config/supportedLocales";
import { format } from "date-fns";
import i18n from "i18n";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { hasAuthority } from "shared/authorization";
import { listReceiptPageable, uploadCsvReceipt } from "shared/network/receipt-summary.api";
import { useGetSessionStorageKey } from "views/Comment/function";
import { TableState } from "views/Company/CompanyList";

export function getFilterAndSearchItemName(filterValue: string) {
  let splittedFilterValue = filterValue.split(";");
  let searchItemNameString = splittedFilterValue.find(value => value.startsWith("searchItemName"));

  let filteredString = filterValue;
  let searchItemNameParam = "";
  if (searchItemNameString) {
    filteredString = filterValue.replace(searchItemNameString + ";", "");

    searchItemNameParam = searchItemNameString.split(":$")?.[1];
  }

  return { filter: filteredString, itemName: searchItemNameParam };
}

const ReceiptList = () => {
  const { t } = useTranslation();
  const { setHeaderButtons } = useHeader();
  const { enqueueSnackbar } = useSnackbar();
  const form = useForm();
  const [loading, setLoading] = useState(false);
  const files = form.watch("files");
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const { account, selectedRelTenant } = useSelector((state: RootState) => state.authentication);
  const { tenant } = selectedRelTenant;
  const [sortState, setSortState] = useState("");
  const [tableState, setTableState] = useState<TableState>({
    page: parseInt(
      window.sessionStorage.getItem(useGetSessionStorageKey("rap-tenantallinvoice-page-number")) ||
        JSON.stringify(0),
    ),
    pageSize: parseInt(
      window.sessionStorage.getItem(useGetSessionStorageKey("rap-tenantallinvoice-page-size")) ||
        JSON.stringify(10),
    ),
    filterOpen: false,
    filterValue: "",
  });
  const { page, pageSize, filterValue } = tableState;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverRow, setPopoverRow] = useState<any>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setPopoverRow(row);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverRow(null);
  };

  const open = Boolean(anchorEl);

  const listReceiptPageableQuery = useQuery(
    ["listReceiptPageableQuery", page, pageSize, tenant.id, filterValue, sortState, selectedStatus],
    async () => {
      const filterContent = getFilterAndSearchItemName(filterValue);

      const { data } = await listReceiptPageable(
        page,
        pageSize,
        tenant.id,
        filterContent?.filter,
        sortState,
      );
      return data;
    },
  );

  const columns: GridColDef[] = [
    {
      field: "cashRegister.serialNumber",
      headerName: "Pénztárgép",
      flex: 1,
      type: "stringContainsNumber",
      valueGetter: ({ row }: GridValueGetterParams) => {
        if (row?.cashRegister?.serialNumber === row?.cashRegister?.name) {
          return row?.cashRegister?.serialNumber || "-";
        } else {
          return (
            (row?.cashRegister?.name || "-") +
            (row?.cashRegister?.serialNumber ? `(${row?.cashRegister?.serialNumber})` : "")
          );
        }
      },
    },
    {
      field: "releaseDate",
      headerName: "Kiállítás dátuma",
      flex: 1,
      valueGetter: ({ row }: GridValueGetterParams) => {
        return row.releaseDate
          ? format(new Date(row.releaseDate), "P", { locale: supportedLocales[i18n.language] })
          : "";
      },
      type: "date",
    },
    {
      field: "grossPrice",
      headerName: "Bruttó összesen",
      flex: 1,
      headerAlign: "right",
      align: "right",
      renderCell: ({ row }: GridValueGetterParams) => {
        return (
          <Box
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
            onClick={e => handlePopoverOpen(e, row)}
          >
            <Typography>
              {t("common:number", {
                num: row.grossPriceA + row.grossPriceB + row.grossPriceC + row.grossPriceE,
              }) +
                " " +
                row.currency.code}
            </Typography>
          </Box>
        );
      },
    },

    {
      field: "currency.code",
      headerName: t("monitoring.machineDetails.currency"),
      flex: 1,
      hide: true,
      valueGetter: ({ row }: GridValueGetterParams) => row.currency.code || "",
    },
    // {
    //   field: "actions",
    //   headerName: " ",
    //   width: 200,
    //   sortable: false,
    //   disableColumnMenu: true,
    //   renderCell: ({ row }: GridRenderCellParams) => (
    //     <Box display="flex" justifyContent="flex-end" width="100%">
    //       {hasAuthority(account.user, account.permissions, selectedRelTenant, ["INVOICE_GET"]) && (
    //         <Tooltip title={t("invoice.details").toString()}>
    //           <IconButton
    //             disabled={row?.status === "DELETED"}
    //             component={Link}
    //             to={`/invoices/invoices-list/invoice-details?id=${row.id}`}
    //             size="small"
    //             color="primary"
    //             style={{ margin: "0 4px" }}
    //           >
    //             <Assignment />
    //           </IconButton>
    //         </Tooltip>
    //       )}
    //     </Box>
    //   ),
    // },
  ];

  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    setHeaderButtons(
      <>
        <Box display="flex" gridGap={12}>
          {hasAuthority(account.user, account.permissions, selectedRelTenant, ["INVOICE_ADMIN"]) &&
            tenant.technicalSignatureKey &&
            (tenant?.address?.country === "HUNGARY" ||
              tenant?.address?.country === "Magyarország") && (
              <Button
                size="small"
                onClick={() => setImportOpen(true)}
                variant="outlined"
                startIcon={<ImportExport />}
              >
                Nyugták importálása
              </Button>
            )}
        </Box>
      </>,
    );

    return () => {
      setHeaderButtons(null);
    };
  }, []);

  async function onSubmit(values: any) {
    setLoading(true);
    try {
      if (values?.files?.[0]) {
        await uploadCsvReceipt(values.files[0], tenant?.id);
      }

      setImportOpen(false);
      listReceiptPageableQuery?.refetch();
      enqueueSnackbar(t("common:notification.upload.success"), {
        variant: "success",
      });
    } catch {
      enqueueSnackbar(t("common:notification.upload.failure"), {
        variant: "error",
      });
    }
    setLoading(false);
  }
  const history = useHistory();
  function onCellClick(params: GridCellParams) {
    if (params.field !== "actions") {
    }
  }

  return (
    <Box mb={2.5}>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {popoverRow && (
          <Box p={2}>
            <Box display="flex">
              <Box minWidth={120}>
                <Typography style={{ textAlign: "right", fontWeight: "bold" }}>ÁFA</Typography>
              </Box>
              <Box minWidth={120}>
                <Typography style={{ textAlign: "right", fontWeight: "bold" }}>Nettó ár</Typography>
              </Box>
              <Box minWidth={120}>
                <Typography style={{ textAlign: "right", fontWeight: "bold" }}>
                  ÁFA érték
                </Typography>
              </Box>
              <Box minWidth={120}>
                <Typography style={{ textAlign: "right", fontWeight: "bold" }}>
                  Bruttó ár
                </Typography>
              </Box>
            </Box>
            {["A", "B", "C", "E"].map(letter =>
              popoverRow[`grossPrice${letter}`] ? (
                <Box display="flex" key={letter}>
                  <Box minWidth={120}>
                    <Typography style={{ textAlign: "right" }}>
                      {popoverRow[`vatPercentage${letter}`]}%
                    </Typography>
                  </Box>
                  <Box minWidth={120}>
                    <Typography style={{ textAlign: "right" }}>
                      {t("common:number", {
                        num: popoverRow[`netPrice${letter}`],
                      })}{" "}
                      {popoverRow.currency?.code}
                    </Typography>
                  </Box>
                  <Box minWidth={120}>
                    <Typography style={{ textAlign: "right" }}>
                      {t("common:number", {
                        num: popoverRow[`vatPrice${letter}`],
                      })}{" "}
                      {popoverRow.currency?.code}
                    </Typography>
                  </Box>
                  <Box minWidth={120}>
                    <Typography style={{ textAlign: "right" }}>
                      {t("common:number", {
                        num: popoverRow[`grossPrice${letter}`],
                      })}{" "}
                      {popoverRow.currency?.code}
                    </Typography>
                  </Box>
                </Box>
              ) : null,
            )}
          </Box>
        )}
      </Popover>
      {importOpen && (
        <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>CSV import</DialogTitle>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <DialogContent style={{ textAlign: "center" }}>
              <Button
                disabled={loading}
                size="small"
                variant="outlined"
                component="label"
                htmlFor="files"
                startIcon={files?.length ? <Edit /> : <Add />}
              >
                {files?.length
                  ? t("personalData.formValues.chooseAnotherFile")
                  : t("personalData.formValues.chooseFile")}
                <input
                  id="files"
                  style={{ display: "none" }}
                  type="file"
                  {...form.register("files", {
                    required: {
                      value: true,
                      message: t("validation.required"),
                    },
                  })}
                />
              </Button>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setImportOpen(false)} variant="text">
                Mégse
              </Button>
              <Button color="primary" variant="contained" type="submit" disabled={loading}>
                {loading ? <CircularProgress style={{ color: "white" }} size={20} /> : "OK"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
      <PageableTable
        filterable={["cashRegister.serialNumber"]}
        sortState={sortState}
        setSortState={setSortState}
        sessionStorageKey="tenantallinvoice"
        tableState={tableState}
        setTableState={setTableState}
        columns={columns}
        query={listReceiptPageableQuery}
        minWidth={{ value: "md" }}
        onCellClick={onCellClick}
      />
    </Box>
  );
};

export default ReceiptList;
